import { Fragment, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bookmark, CalendarDays, CheckCircle2, ChevronRight, FolderKanban, Inbox, Layers3, LayoutDashboard, Map, Menu, Moon, Plus, Search, Settings, Sun, WifiOff, X } from "lucide-react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useBackend } from "../backend/BackendProvider";
import { PreferenceEffects } from "../app/PreferenceEffects";
import { QuickAddDialog } from "./QuickAddDialog";
import { ToastProvider } from "./ToastProvider";
import { dateInTimeZone } from "../app/userDate";
import { RepeatEngine } from "../app/RepeatEngine";
import { WeeklyFocusEngine } from "../app/WeeklyFocusEngine";
import { rememberVisit } from "../app/navigationHistory";
import { useSavedViews } from "../app/savedViews";
import { OfflineSyncEngine } from "../app/OfflineSyncEngine";
import { offlineStateEvent, readOfflineQueue } from "../app/offline";
import { ScheduleMigrationEngine } from "../app/ScheduleMigrationEngine";
import { FeatureRetirementEngine } from "../app/FeatureRetirementEngine";
import { SearchCommandDialog } from "./SearchCommandDialog";

const fixedItems = [
  { to: "/overview", label: "Overview", icon: LayoutDashboard },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/today", label: "Today", icon: Sun },
  { to: "/upcoming", label: "Upcoming", icon: CalendarDays },
  { to: "/anytime", label: "Anytime", icon: Layers3 },
  { to: "/someday", label: "Someday", icon: Moon },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/areas", label: "Areas", icon: Map },
  { to: "/logbook", label: "Logbook", icon: CheckCircle2 },
] as const;

export function AppShell() {
  const backend = useBackend();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const createShortcutLabel = applePlatform() ? "⌘ Enter" : "Ctrl Enter";
  const savedViews = useSavedViews();
  const [online, setOnline] = useState(() => navigator.onLine); const [queuedCount, setQueuedCount] = useState(() => readOfflineQueue().length);
  const profile = useQuery({ queryKey: ["profile"], queryFn: () => backend.profile.get() });
  const preferences = useQuery({ queryKey: ["preferences"], queryFn: () => backend.preferences.get() });
  const areas = useQuery({ queryKey: ["areas"], queryFn: () => backend.areas.list() });
  const inbox = useQuery({ queryKey: ["view", "inbox", "count"], queryFn: () => backend.views.inbox({ limit: 1 }) });
  const todayDate = dateInTimeZone(new Date(), preferences.data?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  const today = useQuery({ queryKey: ["view", "today", "count", todayDate], queryFn: () => backend.views.today(todayDate, { limit: 1 }) });

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => { rememberVisit(location.pathname); }, [location.pathname]);
  useEffect(() => { const refresh = () => { setOnline(navigator.onLine); setQueuedCount(readOfflineQueue().length); }; window.addEventListener("online", refresh); window.addEventListener("offline", refresh); window.addEventListener(offlineStateEvent, refresh); return () => { window.removeEventListener("online", refresh); window.removeEventListener("offline", refresh); window.removeEventListener(offlineStateEvent, refresh); }; }, []);
  useEffect(() => {
    const openQuickAdd = () => setQuickAddOpen(true);
    window.addEventListener("todo-open-quick-add", openQuickAdd);
    return () => window.removeEventListener("todo-open-quick-add", openQuickAdd);
  }, []);
  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      const editing = Boolean(target?.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])"));
      if (event.isComposing || editing) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); setSearchOpen(true); }
      if (!quickAddOpen && (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && event.key === "Enter") { event.preventDefault(); setQuickAddOpen(true); }
    };
    document.addEventListener("keydown", keydown);
    return () => document.removeEventListener("keydown", keydown);
  }, [quickAddOpen]);

  return (
    <ToastProvider>
      <PreferenceEffects />
      <RepeatEngine />
      <WeeklyFocusEngine />
      <OfflineSyncEngine />
      <ScheduleMigrationEngine />
      <FeatureRetirementEngine />
      <div className={`app-shell ${menuOpen ? "menu-open" : ""}`}>
        <button className="mobile-scrim" aria-label="Close navigation" onClick={() => setMenuOpen(false)} />
        <aside className="sidebar" aria-label="Main navigation">
          <div className="brand"><span className="brand-mark">✓</span><span>TODO Web</span></div>
          <button className="quick-add-button" aria-label="New Task" onClick={() => setQuickAddOpen(true)}><span><Plus size={17} />New Task</span><kbd aria-hidden="true">{createShortcutLabel}</kbd></button>
          <nav className="sidebar-scroll">
            <div className="nav-group fixed-navigation">
              {fixedItems.map(({ to, label, icon: Icon }) => <Fragment key={to}>
                <NavLink to={to} end={to === "/areas"} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                  <Icon size={17} /><span>{label}</span>
                  {to === "/inbox" && Boolean(inbox.data?.total) && <span className="nav-count">{inbox.data?.total}</span>}
                  {to === "/today" && Boolean(today.data?.total) && <span className="nav-count">{today.data?.total}</span>}
                </NavLink>
                {to === "/areas" && <div className="area-children" aria-label="Favorite Areas">{areas.data?.map((area) => <NavLink key={area.id} to={`/areas/${area.id}`} className={({ isActive }) => `nav-item area-link ${isActive ? "active" : ""}`}><span className="area-dot" style={{ background: area.color ?? "#8692a4" }} /><span>{area.name}</span><ChevronRight size={14} /></NavLink>)}{areas.isError && <p className="nav-inline-error" role="alert">Unable to load Areas <button type="button" onClick={() => void areas.refetch()}>Retry</button></p>}</div>}
              </Fragment>)}
            </div>
            <div className="nav-group personal-views"><p className="nav-label">Personal Views</p>{savedViews.map((view) => <NavLink key={view.id} to={view.href} className={`${location.pathname}${location.search}` === view.href ? "nav-item active" : "nav-item"}><Bookmark size={15} /><span>{view.name}</span></NavLink>)}<NavLink to="/views" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}><Settings size={15} /><span>Personal Views</span></NavLink></div>
          </nav>
          <NavLink to="/settings/general" className={({ isActive }) => `nav-item sidebar-settings ${isActive ? "active" : ""}`}><Settings size={17} /><span>Settings</span></NavLink>
          <div className="sidebar-footer">
            <div className="avatar">{profile.data?.displayName.slice(0, 1) ?? "U"}</div>
            <div><strong>{profile.data?.displayName ?? "Loading"}</strong><small>{backend.kind === "supabase" ? "Supabase" : "Local demo"}</small></div>
          </div>
        </aside>
        <main className="main">
          <header className="topbar">
            <button className="icon-button mobile-only" aria-label={menuOpen ? "Close navigation" : "Open navigation"} onClick={() => setMenuOpen((value) => !value)}>{menuOpen ? <X size={19} /> : <Menu size={19} />}</button>
            <button ref={searchTriggerRef} className="search-trigger" aria-label="Search" aria-haspopup="dialog" aria-expanded={searchOpen} onClick={() => setSearchOpen(true)}><Search size={16} /><span>Search</span><kbd>⌘ K</kbd></button>
          </header>
          {(!online || queuedCount > 0) && <Link className="offline-banner" to="/offline"><WifiOff size={15} /><span>{online ? `${queuedCount} offline ${queuedCount === 1 ? "change is" : "changes are"} waiting to sync` : `Offline · ${queuedCount} ${queuedCount === 1 ? "change" : "changes"} waiting · Recent content is available`}</span></Link>}
          <Outlet />
        </main>
        <button className="mobile-fab" aria-label="New Task" onClick={() => setQuickAddOpen(true)}><Plus size={22} /></button>
        <QuickAddDialog open={quickAddOpen} onClose={() => setQuickAddOpen(false)} />
        <SearchCommandDialog open={searchOpen} onClose={() => setSearchOpen(false)} returnFocusRef={searchTriggerRef} />
      </div>
    </ToastProvider>
  );
}

function applePlatform() {
  return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
